import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { useHotelRooms, type HotelRoom } from "@/hooks/useHotelRooms";
import { Typography } from "@akuna/ui";
import { STAY_ACCENT } from "./theme";

const HOTEL = STAY_ACCENT.hotel;

function freeCount(room: HotelRoom): number {
  if (room.spaceType === "bed") return room.beds.filter((bed) => !bed.occupantName).length;
  return Math.max(0, (room.capacity ?? 0) - room.occupancy);
}

const StepLabel = ({ n, children }: { n: number; children: ReactNode }) => (
  <div className="akuna-field__label">
    <span className="text-foreground/70">{n} ·</span> {children}
  </div>
);

export function HotelBedSelect({ value, onChange }: { value: { hotelRoomId: string | null; hotelRoomBedId: string | null }; onChange: (value: { hotelRoomId: string | null; hotelRoomBedId: string | null }) => void }) {
  const { t } = useTranslation("daycareHotel");
  const { data: rooms = [], isLoading } = useHotelRooms();
  const [browsedRoomId, setBrowsedRoomId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Typography asChild variant="body-sm">
        <p className="text-muted-foreground">{t("loading")}</p>
      </Typography>
    );
  }

  const selectedRoom =
    rooms.find((room) => room.id === value.hotelRoomId) ??
    rooms.find((room) => room.beds.some((bed) => bed.id === value.hotelRoomBedId)) ??
    rooms.find((room) => room.id === browsedRoomId) ??
    null;

  return (
    <section className="grid gap-4 rounded-2xl border border-border bg-card p-4">
      <Typography variant="h5">
        {t("accommodation")}
      </Typography>

      <div className="grid gap-2">
        <StepLabel n={1}>{t("chooseRoom")}</StepLabel>
        <div className="flex flex-wrap gap-2">
          {rooms.map((room) => {
            const selected = room.id === selectedRoom?.id;
            const dot = room.color ?? HOTEL.solid;
            const free = freeCount(room);
            return (
              <button
                key={room.id}
                type="button"
                disabled={free === 0 && !selected}
                onClick={() => {
                  setBrowsedRoomId(room.id);
                  onChange(room.spaceType === "bed" ? { hotelRoomId: null, hotelRoomBedId: null } : { hotelRoomId: room.id, hotelRoomBedId: null });
                }}
                className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                style={selected ? { borderColor: dot, background: `${dot}14` } : undefined}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: dot }} />
                <Typography asChild variant="body-sm">
                  <span className="font-semibold text-foreground">{room.name}</span>
                </Typography>
                <Typography asChild variant="caption">
                  <span>{t("freeCount", { count: free })}</span>
                </Typography>
              </button>
            );
          })}
        </div>
      </div>

      {selectedRoom && selectedRoom.spaceType === "bed" && (
        <div className="grid gap-2">
          <StepLabel n={2}>{t("chooseBed")} · {selectedRoom.name}</StepLabel>
          <div className="grid gap-2 sm:grid-cols-3">
            {selectedRoom.beds.map((bed) => {
              const taken = !!bed.occupantName;
              const selected = bed.id === value.hotelRoomBedId;
              const accent = selectedRoom.color ?? HOTEL.solid;
              return (
                <button
                  key={bed.id}
                  type="button"
                  disabled={taken}
                  onClick={() => onChange({ hotelRoomId: null, hotelRoomBedId: bed.id })}
                  className={`grid gap-0.5 rounded-xl border p-3 text-left text-sm transition-colors ${taken ? "border-dashed opacity-70" : ""}`}
                  style={selected ? { borderColor: accent, background: `${accent}14` } : undefined}
                >
                  <Typography asChild variant="body-sm">
                    <span className="flex items-center justify-between font-semibold text-foreground">
                      {bed.name}
                      {selected && <Check size={15} style={{ color: accent }} />}
                    </span>
                  </Typography>
                  <Typography asChild variant="caption">
                    <span style={{ color: taken ? undefined : accent }}>
                      {taken ? `${t("occupied")} · ${bed.occupantName}` : t("bedFree")}
                    </span>
                  </Typography>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
